// @ts-check
import React from 'react';
import { Route, IndexRoute } from 'react-router';

import { fetchCurrentAgent } from './actions/CurrentAgentActions';

import NoMatch from 'shared/components/NoMatch';
import AgentPortalLayout from './layouts/AgentPortalLayout';
import RootLayout from './layouts/RootLayout';
import DashboardPage from './components/DashboardPage';
import * as AgentPortalReferralProfiles from './components/ReferralProfiles';
import ReferralQuestionnairePage from './components/ReferralQuestionnairePage';
import CalendarNewPage from './components/CalendarNewPage';
import CostEstimatesPage from './components/CostEstimatesPage';
import AnalyticsPage from './components/AnalyticsPage';
import * as Subscriptions from './components/Subscriptions';
import * as Referrals from './components/Referrals';
import * as EligibilityChecks from './components/EligibilityChecks';
import * as CurrentAgent from './components/CurrentAgent';
import * as Providers from './components/Providers';
import * as PatientMembers from './components/PatientMembers';
import * as Faxes from 'shared/components/Referrals/Faxes';
import NewReferralWizardWrapper from 'shared/components/NewReferralWizardWrapper';
import * as InsuranceCoverages from 'shared/components/InsuranceCoverages';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import * as Organizations from './components/Organizations';
// import OrganizationSettingsPage from './components/OrganizationSettingsPage';
import { OrganizationSettingsPage } from './screens/EntitySettings/OrganizationSettingsScreen';
import EntitySettings from './components/OrganizationSettingsPage/EntitySettings';
import ChooseEntityNotice from './components/OrganizationSettingsPage/ChooseEntityNotice';
import ChooseSettingNotice from './components/OrganizationSettingsPage/ChooseSettingNotice';
import * as Agents from './components/Agents';
import * as StaffEmployments from './components/StaffEmployments';
import * as Entities from './components/Entities';
import { ReferralTagsScreen } from './screens/EntitySettings/ReferralTagsScreen';

function configureRoutes(store) {
  const ensureCurrentAgentFetched = (nextState, replace, callback) => {
    const { currentAgent } = store.getState();

    if (!currentAgent.id) {
      fetchCurrentAgent(store.dispatch).then((response) => {
        if (
          response.data.currentAgent.needsPasswordChange &&
          !response.data.currentAgent.masquerading
        ) {
          FlashMessageActions.flashWarning({
            message: 'Before you continue, please update your password',
          });
          replace({ pathname: '/a/portal/current_agent/edit/change_password' });
        }
        callback();
      });
    } else {
      callback();
    }
  };

  return (
    <Route path="/" onEnter={ensureCurrentAgentFetched} component={RootLayout}>
      <Route path="a">
        <Route path="calendar" component={CalendarNewPage} />
        <Route path="analytics" component={AnalyticsPage} />
        <Route path="portal" component={AgentPortalLayout}>
          <Route path="agents/:agent_id">
            <Route
              path="login_attempts"
              component={Agents.LoginAttempts.IndexPage}
            />
          </Route>
          <Route path="current_agent/edit" component={CurrentAgent.Edit} />
          <Route
            path="current_agent/edit/change_password"
            component={CurrentAgent.Edit}
            defaultTabName="Change Password"
          />
          <Route path="current_provider_employment">
            <Route path="ruleset/edit" component={Providers.Ruleset.Edit} />
            <Route
              path="branded_ruleset/edit"
              component={Providers.BrandedRuleset.Edit}
            />
          </Route>
          <Route path="dashboard" component={DashboardPage} />
          <Route
            path="eligibility_checks/new"
            component={EligibilityChecks.New}
          />
          <Route
            path="insurance_coverages/:id"
            component={InsuranceCoverages.Show}
            apiEndpoint="/agent_portal/api"
          />
          <Route path="subscribe" component={Subscriptions.New} />
          <Route path="staff_employments">
            <Route path=":staff_employment_id">
              <IndexRoute component={StaffEmployments.Show} />
            </Route>
          </Route>
          <Route path="entity_settings" component={OrganizationSettingsPage}>
            <IndexRoute component={ChooseEntityNotice} />
            <Route path=":entityId" component={EntitySettings}>
              <IndexRoute component={ChooseSettingNotice} />
              <Route
                path="notification_preferences"
                component={Entities.NotificationPreferences.Edit}
              />
              <Route
                path="referral_checklist_template"
                component={Entities.ReferralChecklistTemplate.Edit}
              />
              <Route
                path="accepting_new_patients"
                component={Entities.AcceptingNewPatientsForm}
              />
              <Route
                path="questionnaire_templates"
                component={Organizations.QuestionnaireTemplates.IndexPage}
              />
              <Route path="referral_tags" component={ReferralTagsScreen} />
            </Route>
          </Route>
          <Route path="organizations/:organization_id">
            <Route
              path="providers"
              component={Organizations.Providers.IndexPage}
            />
            <Route path="users" component={Organizations.Users.IndexPage} />
            <Route path="users/new" component={Organizations.Users.New} />
          </Route>
          <Route
            path="passive_app_notifications"
            component={CurrentAgent.PassiveAppNotifications.Main}
          >
            <Route
              path="unread"
              component={CurrentAgent.PassiveAppNotifications.Unread}
            />
            <Route
              path="all"
              component={CurrentAgent.PassiveAppNotifications.All}
            />
          </Route>
          <Route path="patient_members">
            <IndexRoute component={PatientMembers.IndexPage} />
            <Route path="new" component={PatientMembers.New} />
            <Route path=":id" component={PatientMembers.Show} />
            <Route path=":id/edit" component={PatientMembers.Edit} />
          </Route>
          <Route path="providers">
            <Route path="network" component={Providers.Network.Show} />
            <Route
              path=":providerId/profile"
              component={Providers.Profile.Show}
            />
            <Route
              path="insurance_rejections"
              component={Providers.InsuranceRejections.Show}
            />
          </Route>
          <Route path="referrals">
            <IndexRoute
              primaryTab="received"
              secondaryTab="pending"
              component={Referrals.IndexPage}
            />
            <Route path="sent">
              <IndexRoute
                primaryTab="sent"
                secondaryTab="pending"
                component={Referrals.IndexPage}
              />
              <Route
                path="scheduled"
                primaryTab="sent"
                secondaryTab="scheduled"
                component={Referrals.IndexPage}
              />
              <Route
                path="all"
                primaryTab="sent"
                secondaryTab="all"
                component={Referrals.IndexPage}
              />
              <Route
                path="rejected"
                primaryTab="sent"
                secondaryTab="rejected"
                component={Referrals.IndexPage}
              />
              <Route
                path="search"
                primaryTab="sent"
                secondaryTab="search"
                component={Referrals.IndexPage}
              />
            </Route>
            <Route path="received">
              <IndexRoute
                primaryTab="received"
                secondaryTab="pending"
                component={Referrals.IndexPage}
              />
              <Route
                path="confirmed"
                primaryTab="received"
                secondaryTab="confirmed"
                component={Referrals.IndexPage}
              />
              <Route
                path="all"
                primaryTab="received"
                secondaryTab="all"
                component={Referrals.IndexPage}
              />
              <Route
                path="rejected"
                primaryTab="received"
                secondaryTab="rejected"
                component={Referrals.IndexPage}
              />
              <Route
                path="search"
                primaryTab="received"
                secondaryTab="search"
                component={Referrals.IndexPage}
              />
            </Route>
          </Route>
          <Route path="referrals/:referralId" component={Referrals.Show} />
          <Route path="referrals/:referralId">
            <IndexRoute component={Referrals.Show} />
            <Route
              path="faxes/new"
              component={Faxes.New}
              apiEndpoint="/agent_portal/api"
              webpageBase="/a/portal"
            />
          </Route>
          <Route path="referral_profiles">
            <Route
              path="new"
              component={NewReferralWizardWrapper}
              stepComponent={AgentPortalReferralProfiles.New}
              step={1}
            />
            <Route path=":referralProfileId">
              <Route
                path="referral_questionnaire"
                component={NewReferralWizardWrapper}
                stepComponent={ReferralQuestionnairePage}
                step={2}
              />
              <Route
                path="referral_dashboard"
                component={NewReferralWizardWrapper}
                stepComponent={
                  AgentPortalReferralProfiles.ReferralDashboard.Show
                }
                step={3}
              />
              <Route
                path="referral_confirmation"
                component={NewReferralWizardWrapper}
                stepComponent={
                  AgentPortalReferralProfiles.ReferralConfirmationPage
                }
                step={4}
                apiEndpoint="/agent_portal/api"
                webpageBase="/a/portal"
              />
            </Route>
          </Route>
          <Route path="cost_estimates">
            <Route path="new" component={CostEstimatesPage} />
          </Route>
        </Route>
      </Route>
      <Route path="*" component={NoMatch} />
    </Route>
  );
}

export default configureRoutes;
